import { apiClient } from './client'
import type { EditorJsData, LessonNote, LessonNoteEditingState, LessonNoteVisibility, LessonPreviousNote } from '@/types'

type ApiWrapper<T> = {
  message?: string
  data?: T
}

type IndexResponse<T> =
  | T[]
  | {
      data?: T[]
      message?: string
    }

function unwrapIndex<T>(raw: IndexResponse<T>): T[] {
  if (Array.isArray(raw)) return raw
  if (raw?.data && Array.isArray(raw.data)) return raw.data
  return []
}

export const lessonNotesApi = {
  getNotes: async (lessonId: number): Promise<LessonNote[]> => {
    const response = await apiClient.get<IndexResponse<LessonNote>>(`/lessons/${lessonId}/notes`)
    return unwrapIndex(response.data)
  },

  getPreviousNote: async (lessonId: number): Promise<LessonPreviousNote | null> => {
    const response = await apiClient.get<{ data: LessonPreviousNote | null }>(`/lessons/${lessonId}/previous-note`)
    return response.data?.data ?? null
  },

  createNote: async (lessonId: number, payload: { visibility: LessonNoteVisibility; body_json: EditorJsData }): Promise<LessonNote> => {
    const response = await apiClient.post<ApiWrapper<LessonNote>>(`/lessons/${lessonId}/notes`, payload)
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка создания заметки урока')
  },

  updateNote: async (noteId: number, payload: { visibility?: LessonNoteVisibility; body_json: EditorJsData }): Promise<LessonNote> => {
    const response = await apiClient.patch<ApiWrapper<LessonNote>>(`/lesson-notes/${noteId}`, payload)
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления заметки урока')
  },

  deleteNote: async (noteId: number): Promise<void> => {
    await apiClient.delete<ApiWrapper<unknown>>(`/lesson-notes/${noteId}`)
  },

  /** Кто сейчас держит фокус редактирования заметки (для блокировки и сокетов) */
  getNoteEditingState: async (lessonId: number): Promise<LessonNoteEditingState> => {
    const response = await apiClient.get<{ data: LessonNoteEditingState }>(`/lessons/${lessonId}/note-editing`)
    return response.data?.data ?? { shared: null, private_teacher: null }
  },

  setNoteEditing: async (
    lessonId: number,
    visibility: LessonNoteVisibility,
    editing: boolean,
    bodyJson?: EditorJsData
  ): Promise<LessonNoteEditingState & { acquired?: boolean }> => {
    const response = await apiClient.put<{ data: LessonNoteEditingState & { acquired?: boolean } }>(
      `/lessons/${lessonId}/note-editing`,
      { visibility, editing, body_json: bodyJson }
    )
    if (!response.data?.data) {
      return { shared: null, private_teacher: null }
    }
    return response.data.data
  },

  heartbeatNoteEditing: async (lessonId: number, visibility: LessonNoteVisibility, bodyJson?: EditorJsData): Promise<void> => {
    await apiClient.post(`/lessons/${lessonId}/note-editing/heartbeat`, { visibility, body_json: bodyJson })
  },
}

